// import { useGameSettings } from '../contexts/GameSettingsContext'
import { GameStats as GameStatsType } from '../types/game'
import { memo } from 'react'

interface GameStatsProps {
  stats: GameStatsType
}

// Use a constant for maxGuesses to avoid dependency on settings context
const MAX_GUESSES = 6

// Memoize component to prevent unnecessary re-renders
export const GameStats = memo(({ stats }: GameStatsProps) => {
  // const { settings } = useGameSettings()
  // const maxGuesses = settings.selectedMode

  return (
    <div
      className="w-full text-white"
      role="region"
      aria-label="Game Statistics"
    >
      {/* Summary Statistics */}
      <div className="grid grid-cols-2 gap-3 text-center mb-5">
        <StatBox label="Played" value={stats.gamesPlayed} />
        <StatBox
          label="Win %"
          value={Math.round((stats.gamesWon / stats.gamesPlayed) * 100) || 0}
        />
        <StatBox label="Current Streak" value={stats.currentStreak} />
        <StatBox label="Max Streak" value={stats.maxStreak} />
      </div>

      {/* Guess Distribution */}
      <div className="mt-6">
        <h3 className="text-base font-semibold text-center mb-3 text-white/80">
          Guess Distribution
        </h3>
        <div className="space-y-2.5">
          {Array.from({ length: MAX_GUESSES }, (_, i) => i + 1).map(
            (guessNum) => (
              <GuessBar
                key={guessNum}
                guessNumber={guessNum}
                count={stats.guessDistribution[guessNum] || 0}
                maxCount={Math.max(
                  ...Object.values(stats.guessDistribution),
                  1
                )}
              />
            )
          )}
        </div>
      </div>
    </div>
  )
})

GameStats.displayName = 'GameStats'

const StatBox = memo(({ label, value }: { label: string; value: number }) => (
  <div
    className="p-3.5 rounded-lg text-center flex flex-col justify-center items-center"
    style={{
      background:
        'linear-gradient(145deg, rgba(30, 30, 30, 0.6), rgba(20, 20, 20, 0.8))',
      border: '1px solid rgba(80, 80, 80, 0.2)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    }}
  >
    <div className="text-2xl font-bold text-white mb-1">{value}</div>
    <div className="text-xs text-white/60 font-medium">{label}</div>
  </div>
))

StatBox.displayName = 'StatBox'

const GuessBar = memo(
  ({
    guessNumber,
    count,
    maxCount,
  }: {
    guessNumber: number
    count: number
    maxCount: number
  }) => {
    const percentage = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0

    return (
      <div className="flex items-center gap-2.5">
        <div className="w-4 text-white/70 font-semibold">{guessNumber}</div>
        <div className="flex-grow h-8 bg-white/5 rounded-md overflow-hidden">
          <div
            style={{
              width: `${percentage}%`,
              background: `linear-gradient(90deg, #b01018, #ed1c24)`,
              transition: 'width 0.5s ease-out',
            }}
            className="h-full flex items-center"
            role="progressbar"
            aria-valuenow={count}
            aria-valuemin={0}
            aria-valuemax={maxCount}
          >
            <span className="px-3 text-white leading-8 font-medium min-w-[30px] text-shadow">
              {count}
            </span>
          </div>
        </div>
      </div>
    )
  }
)

GuessBar.displayName = 'GuessBar'
